import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { usePlateEditorState } from '@udecode/plate';
import { ReactEditor } from 'slate-react';

import AgentMentionList from './agent_mention_list';
import AgentSearch from 'components/agents/agent_search';
import connect from 'components/lib/connect';

// Renders the agent search menu while an "@search" is active. Mirrors the old text_editor menu, but
// reads the active search via `editor.getAgentMentionText()` (Plate) and anchors itself to the rendered
// search-mark DOM node (`[data-agent-mention-search]`) rather than a passed-in ref.
export default function AgentMentionMenu({ autoPosition, height, menuActionsRef, position, readOnly }) {
  const editor = usePlateEditorState();

  // Keep a ref to the current editor so the menu's stable callbacks (registered on menuActionsRef) and
  // child items can call editor commands without being recreated on every change.
  const editorRef = useRef(editor);
  editorRef.current = editor;

  const itemsRef = useRef(null);
  const [selectedIndex, dispatch] = useRegisterMenuActions(editorRef, menuActionsRef, itemsRef);

  const searchText = editor ? editor.getAgentMentionText() : null;
  const targetRef = useAgentMentionSearchAnchor(editor, searchText);

  if (readOnly || !editor || searchText == null || !targetRef.current) {
    return null;
  }

  return (
    <AgentSearcherContainer
      autoPosition={autoPosition}
      dispatch={dispatch}
      editorRef={editorRef}
      height={height}
      itemsRef={itemsRef}
      mentionSearchRef={targetRef}
      position={position}
      searchText={searchText}
      selectedIndex={selectedIndex}
    />
  );
}

AgentMentionMenu.propTypes = {
  autoPosition: PropTypes.bool,
  height: PropTypes.string,
  menuActionsRef: PropTypes.shape({ current: PropTypes.any }).isRequired,
  position: PropTypes.string,
  readOnly: PropTypes.bool,
};

// Locate the rendered search-mark span so the menu popover can anchor to it. We resolve this in an
// effect (after the commit) because slate-react updates the DOM out-of-band, so the node may not exist
// yet during render. Scope the query to this editor's DOM to avoid colliding with other editors.
function useAgentMentionSearchAnchor(editor, searchText) {
  const targetRef = useRef(null);
  const [, rerender] = useState(null);

  useEffect(() => {
    let node = null;
    if (editor && searchText != null) {
      try {
        const editorNode = ReactEditor.toDOMNode(editor, editor);
        node = editorNode?.querySelector('[data-agent-mention-search]') || null;
      } catch (err) {
        node = null;
      }
    }

    if (targetRef.current !== node) {
      targetRef.current = node;
      rerender({});
    }
  });

  return targetRef;
}

// The plugin's onKeyDown intercepts up/down/enter/tab while a search is active, but the selected index
// lives here. Rather than hoisting that state into the editor, we register callbacks on a shared ref so
// the plugin can drive the menu directly. (See the original text_editor plugin for the rationale.)
function useRegisterMenuActions(editorRef, menuActionsRef, itemsRef) {
  const [selectedIndex, dispatch] = useReducer(selectionReducer, 0);

  const selectedIndexRef = useRef(selectedIndex);
  useEffect(() => {
    selectedIndexRef.current = selectedIndex;
  }, [selectedIndex]);

  useEffect(() => {
    menuActionsRef.current = {
      ...menuActionsRef.current,
      onArrowDown: () => {
        dispatch({ type: 'down', numItems: itemsRef.current?.length || 0 });
      },
      onArrowUp: () => {
        dispatch({ type: 'up' });
      },
      onInsert: () => {
        const item = itemsRef.current?.[selectedIndexRef.current];
        if (!item) {
          return false;
        }
        editorRef.current.completeAgentMentionSearch(item);
        return true;
      },
    };
  }, [editorRef, itemsRef, menuActionsRef]);

  return [selectedIndex, dispatch];
}

function selectionReducer(state, action) {
  switch (action.type) {
    case 'up':
      return state > 0 ? state - 1 : 0;
    case 'down':
      return state < action.numItems - 1 ? state + 1 : state;
    case 'set':
      return action.index;
    default:
      throw new Error();
  }
}

const AgentSearcherContainer = connect(mapStateToProps)(AgentSearcher);

function mapStateToProps({ getProvider }) {
  const agentsProvider = getProvider('agents');
  const recentlyMentionedAgentIds = getProvider('recentlyMentionedAgentIds').findAll();
  const sortedRecents = _.sortBy(recentlyMentionedAgentIds, r => r.updatedAt).reverse();
  const recentlyMentionedAgents = _.compact(
    _.map(sortedRecents, recent => {
      return agentsProvider.findBy({ id: recent.id });
    })
  );

  return {
    currentAgentId: getProvider('currentAgent').get().id,
    recentlyMentionedAgents,
  };
}

export function AgentSearcher({
  autoPosition,
  currentAgentId,
  dispatch,
  editorRef,
  height,
  itemsRef,
  mentionSearchRef,
  position,
  recentlyMentionedAgents,
  searchText,
  selectedIndex,
}) {
  const excludedAgentIds = useMemo(() => {
    return [currentAgentId];
  }, [currentAgentId]);

  return (
    <AgentSearch excludedAgentIds={excludedAgentIds}>
      {({ agentSearch }) => (
        <AgentMentionList
          autoPosition={autoPosition}
          dispatch={dispatch}
          editorRef={editorRef}
          height={height}
          itemsRef={itemsRef}
          mentionSearchRef={mentionSearchRef}
          position={position}
          recentlyMentionedAgents={recentlyMentionedAgents}
          search={agentSearch}
          searchText={searchText}
          selectedIndex={selectedIndex}
        />
      )}
    </AgentSearch>
  );
}
